'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useCookieState } from 'ahooks';
import _ from 'lodash';
import { redirect } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { authApi } from '@/api';
import { Alert, Button, Password, Text } from '@/components';
import { pages } from '@/settings';
import { useGlobalStore } from '@/store/provider';
import type { Auth } from '@/types';
const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(1, 'Password is required')
});
export function SignInWithEmail({
  afterSignInAction
}: Auth.SignInProps) {
  const [myLoc, setMyLoc] = useCookieState('vehicle_loc');
  const {
    user,
    setUser
  } = useGlobalStore(state => state);
  const [isRequestToken, setIsRequestToken] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  // react-hook-form
  const {
    register,
    handleSubmit,
    getValues,
    formState: {
      errors
    }
  } = useForm({
    resolver: zodResolver(schema)
  });

  // submit
  const {
    mutate,
    error,
    isPending,
    reset
  } = useMutation({
    mutationFn: signIn
  });
  function onFormSubmit(data: Auth.SignInRequest) {
    mutate(data);
  }

  // signIn function
  async function signIn(data_0: Auth.SignInRequest) {
    try {
      const d = await authApi.signIn({
        ...data_0
      });
      if (d?.data) {
        setUser(d.data, null);
        // set default zip code & radius
        if (!myLoc && !_.isEmpty(d?.data?.location)) {
          setMyLoc(JSON.stringify({
            zip_code: d.data?.location?.zip_code,
            radius: 200
          }));
        }
      }
    } catch (err) {
      return Promise.reject(err);
    }
  }
  useEffect(() => {
    if (_.isEmpty(user)) return;
    if (afterSignInAction) {
      afterSignInAction();
    } else {
      redirect(pages.myProfile.href);
    }
  }, [user?._id]);
  async function requestToken() {
    if (isRequestToken) return;
    try {
      setIsRequestToken(true);
      await authApi.requestVerificationEmail(getValues('email'));
      setIsEmailSent(true);
      setIsRequestToken(false);
    } catch {
      setIsRequestToken(false);
    }
  }
  const renderEmailNotVerified = () => {
    const email = getValues('email');
    if (isEmailSent) {
      return <>
          We have sent an email to <span className='font-semibold'>{email}</span>
          <br />
          Please check your email & activate your Cruz account.
          <Button type='button' className='mt-4 w-full' variant='primary' onClick={() => {
          setIsEmailSent(false);
          reset();
        }}>
            Ok
          </Button>
        </>;
    }
    return <>
        <h2>Error</h2>
        <p>
          Your email (<span className='font-semibold'>{email}</span>) is not verified. Please check your inbox and verify your email
          address.
        </p>
        <p>If you did not receive the verification email, please check your spam folder or request a new one.</p>
        <Button type='button' className='mt-4 w-full' variant='primary' working={isRequestToken} onClick={requestToken}>
          Request Verification Email
        </Button>
      </>;
  };
  if (error && _.get(error, 'status') === 470) {
    return <div className='mb-8 rounded border px-6 py-4 text-left text-base/7 font-normal'>{renderEmailNotVerified()}</div>;
  }
  return <form noValidate autoComplete='off' className='space-y-3 mb-6' onSubmit={handleSubmit(onFormSubmit)}>
      <Text label='Email Address' type='email' {...register('email')} data-error={_.get(errors, 'email.message')} />
      <Password {...register('password')} data-error={_.get(errors, 'password.message')} />
      <div className='text-center'>
        {error?.message && _.get(error, 'status') !== 470 && <Alert variant='error' message={error.message} />}
        <Button type='submit' className='mt-4 mb-1 w-full' variant='primary' working={isPending}>
          Sign In
        </Button>
      </div>
    </form>;
}